import React, { useState, useEffect, useCallback } from 'react';
import { Trophy, Target, Brain, Users, Crown, ChevronRight, CheckCircle, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DailyTournamentPage() {
  const [tournament, setTournament] = useState(null);
  const [userEntry, setUserEntry] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const [activeTab, setActiveTab] = useState('today');

  const token = localStorage.getItem('token');

  const fetchTournament = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/daily-tournament/today`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTournament(data.tournament);
        setUserEntry(data.user_entry);
        setLeaderboard(data.leaderboard);
      }
    } catch (err) {
      console.error('Error fetching tournament:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/daily-tournament/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, [token]);

  useEffect(() => {
    fetchTournament();
    fetchHistory();
  }, [fetchTournament, fetchHistory]);

  const handlePredictionChange = (matchIndex, prediction) => {
    setPredictions(prev => ({ ...prev, [matchIndex]: prediction }));
  };

  const handleSubmit = async () => {
    const predArray = Object.entries(predictions).map(([idx, pred]) => ({
      match_index: parseInt(idx),
      prediction: pred
    }));

    if (predArray.length !== 5) {
      alert('Bitte alle 5 Matches vorhersagen!');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/daily-tournament/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(predArray)
      });

      if (res.ok) {
        const data = await res.json();
        setResult(data);
        fetchTournament();
      } else {
        const err = await res.json();
        alert(err.detail || 'Fehler beim Einreichen');
      }
    } catch (err) {
      console.error('Submit error:', err);
      alert('Netzwerkfehler');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white p-4 md:p-8" data-testid="daily-tournament-page">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center gap-3 mb-4">
          <Trophy className="w-8 h-8 text-yellow-400" />
          <h1 className="text-3xl font-bold">Daily Tournament</h1>
        </div>
        <p className="text-gray-400">Taeglich neue Matches. Tritt gegen die KI an!</p>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('today')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'today' 
                ? 'bg-teal-500 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Heute
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'history' 
                ? 'bg-teal-500 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Verlauf
          </button>
          <button
            onClick={() => setActiveTab('leaderboard')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              activeTab === 'leaderboard' 
                ? 'bg-teal-500 text-white' 
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
          >
            Rangliste
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        {/* TODAY TAB */}
        {activeTab === 'today' && tournament && (
          <div className="space-y-6">
            {/* Tournament Info */}
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-teal-400" />
                  <span className="text-gray-400">Tournament vom {tournament.date}</span>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${
                  tournament.status === 'active' 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-gray-500/20 text-gray-400'
                }`}>
                  {tournament.status === 'active' ? 'Aktiv' : 'Beendet'}
                </span>
              </div>

              <div className="flex gap-4">
                <div className="bg-gray-800 rounded-lg p-4 flex-1">
                  <Users className="w-6 h-6 text-blue-400 mb-2" />
                  <div className="text-2xl font-bold">{tournament.participants || 0}</div>
                  <div className="text-sm text-gray-400">Teilnehmer</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 flex-1">
                  <Target className="w-6 h-6 text-purple-400 mb-2" />
                  <div className="text-2xl font-bold">5</div>
                  <div className="text-sm text-gray-400">Matches</div>
                </div>
              </div>
            </div>

            {/* Result Display */}
            {(result || userEntry?.submitted) && (
              <div className="bg-gradient-to-r from-teal-900/30 to-purple-900/30 rounded-xl p-6 border border-teal-500/30">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  {(result?.winner || userEntry?.winner) === 'user' ? (
                    <>
                      <Crown className="w-6 h-6 text-yellow-400" />
                      Du hast gewonnen!
                    </>
                  ) : (result?.winner || userEntry?.winner) === 'ai' ? (
                    <>
                      <Brain className="w-6 h-6 text-purple-400" />
                      KI hat gewonnen
                    </>
                  ) : (
                    <>
                      <Target className="w-6 h-6 text-gray-400" />
                      Unentschieden!
                    </>
                  )}
                </h3>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-teal-400">
                      {result?.score ?? userEntry?.score ?? 0}
                    </div>
                    <div className="text-sm text-gray-400">Deine Treffer</div>
                  </div>
                  <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                    <div className="text-3xl font-bold text-purple-400">
                      {result?.ai_score ?? userEntry?.ai_score ?? 0}
                    </div>
                    <div className="text-sm text-gray-400">KI Treffer</div>
                  </div>
                </div>

                {result?.xp_gained && (
                  <div className="text-center text-yellow-400">
                    +{result.xp_gained} XP verdient!
                  </div>
                )}
              </div>
            )}

            {/* Matches */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Target className="w-5 h-5 text-teal-400" />
                Heutige Matches
              </h3>

              {tournament.matches?.map((match, idx) => (
                <div 
                  key={idx} 
                  className="bg-gray-900 rounded-xl p-4 border border-gray-800"
                  data-testid={`match-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500">{match.league} • {match.sport}</span>
                    <span className="text-xs text-gray-500">Match {idx + 1}</span>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div className="text-center flex-1">
                      <div className="font-semibold">{match.home_team}</div>
                      <div className="text-xs text-gray-500">Heim</div>
                    </div>
                    <div className="text-gray-500 mx-4">vs</div>
                    <div className="text-center flex-1">
                      <div className="font-semibold">{match.away_team}</div>
                      <div className="text-xs text-gray-500">Auswaerts</div>
                    </div>
                  </div>

                  {/* Prediction buttons */}
                  {!userEntry?.submitted ? (
                    <div className="flex gap-2">
                      {['home', 'draw', 'away'].map(pred => (
                        <button
                          key={pred}
                          onClick={() => handlePredictionChange(idx, pred)}
                          className={`flex-1 py-2 rounded-lg font-medium transition ${
                            predictions[idx] === pred
                              ? 'bg-teal-500 text-white'
                              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                          }`}
                        >
                          {pred === 'home' ? '1' : pred === 'draw' ? 'X' : '2'}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">Dein Tipp:</span>
                        <span className={`font-semibold ${
                          userEntry.results?.[idx]?.user_correct ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {userEntry.predictions?.[idx] === 'home' ? '1' : 
                           userEntry.predictions?.[idx] === 'draw' ? 'X' : '2'}
                          {userEntry.results?.[idx]?.user_correct ? (
                            <CheckCircle className="inline w-4 h-4 ml-1" />
                          ) : (
                            <XCircle className="inline w-4 h-4 ml-1" />
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400">KI:</span>
                        <span className={`font-semibold ${
                          userEntry.results?.[idx]?.ai_correct ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {tournament.ai_predictions?.[idx]?.prediction === 'home' ? '1' : 
                           tournament.ai_predictions?.[idx]?.prediction === 'draw' ? 'X' : '2'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-400">Ergebnis: </span>
                        <span className="font-semibold">
                          {match.actual_home_score} : {match.actual_away_score}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Submit button */}
            {!userEntry?.submitted && (
              <button
                onClick={handleSubmit}
                disabled={submitting || Object.keys(predictions).length !== 5}
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold rounded-xl 
                         hover:from-teal-400 hover:to-cyan-400 transition disabled:opacity-50 disabled:cursor-not-allowed
                         flex items-center justify-center gap-2"
                data-testid="submit-predictions"
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                    Einreichen...
                  </>
                ) : (
                  <>
                    Vorhersagen einreichen
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && history && (
          <div className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="text-2xl font-bold text-teal-400">{history.stats?.total_tournaments || 0}</div>
                <div className="text-sm text-gray-400">Tournaments</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="text-2xl font-bold text-green-400">{history.stats?.user_wins || 0}</div>
                <div className="text-sm text-gray-400">Siege</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="text-2xl font-bold text-red-400">{history.stats?.ai_wins || 0}</div>
                <div className="text-sm text-gray-400">KI Siege</div>
              </div>
              <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="text-2xl font-bold text-yellow-400">{history.stats?.win_rate || 0}%</div>
                <div className="text-sm text-gray-400">Siegrate</div>
              </div>
            </div>

            {/* Entry list */}
            <div className="space-y-3">
              {history.entries?.map((entry, idx) => (
                <div key={idx} className="bg-gray-900 rounded-xl p-4 border border-gray-800 flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Tournament {entry.tournament_id?.split('_')[1]}</div>
                    <div className="text-sm text-gray-400">{entry.submitted_at?.split('T')[0]}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <div className="text-lg font-bold text-teal-400">{entry.score}</div>
                      <div className="text-xs text-gray-500">Du</div>
                    </div>
                    <div className="text-gray-500">:</div>
                    <div className="text-center">
                      <div className="text-lg font-bold text-purple-400">{entry.ai_score}</div>
                      <div className="text-xs text-gray-500">KI</div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm ${
                      entry.winner === 'user' 
                        ? 'bg-green-500/20 text-green-400' 
                        : entry.winner === 'ai'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {entry.winner === 'user' ? 'Sieg' : entry.winner === 'ai' ? 'Niederlage' : 'Remis'}
                    </div>
                  </div>
                </div>
              ))}

              {(!history.entries || history.entries.length === 0) && (
                <div className="text-center text-gray-500 py-8">
                  Noch keine Teilnahmen. Starte jetzt!
                </div>
              )}
            </div>
          </div>
        )}

        {/* LEADERBOARD TAB */}
        {activeTab === 'leaderboard' && (
          <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="p-4 bg-gray-800 flex items-center gap-2">
              <Crown className="w-5 h-5 text-yellow-400" />
              <h3 className="font-semibold">Globale Rangliste</h3>
            </div>

            <div className="divide-y divide-gray-800">
              {leaderboard.map((entry, idx) => (
                <div key={idx} className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-gray-400 text-black' :
                      idx === 2 ? 'bg-orange-600 text-white' :
                      'bg-gray-700 text-gray-300'
                    }`}>
                      {idx + 1}
                    </div>
                    <div>
                      <div className="font-semibold">{entry.user_email}</div>
                      <div className="text-sm text-gray-400">{entry.tournaments_played || 0} Tournaments</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-teal-400">{entry.score || 0} Pkt</div>
                    <div className="text-sm text-gray-500">{entry.wins || 0} Siege</div>
                  </div>
                </div>
              ))}

              {leaderboard.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  Noch keine Eintraege.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
